import { formatDistanceToNow } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";

const UPDATE_INTERVAL_MS = 30_000;

function formatTimestamp(date: Date): string {
  const timeDifferenceInMs = Date.now() - date.getTime();

  if (timeDifferenceInMs < 60_000) {
    return "Last checked: just now";
  }

  return `Last checked: ${formatDistanceToNow(date)} ago`;
}

interface UseLastCheckedTimestampResult {
  formattedTimestamp: string;
  refresh: () => void;
}

/**
 * Tracks a "Last checked" timestamp that updates:
 * - On initial mount (set to "just now")
 * - When `refresh` is called (e.g. on pull-to-refresh)
 * - Every 30 seconds to keep the relative time display current
 */
export function useLastCheckedTimestamp(): UseLastCheckedTimestampResult {
  const lastCheckedRef = useRef(new Date());
  const [formattedTimestamp, setFormattedTimestamp] = useState(() =>
    formatTimestamp(lastCheckedRef.current)
  );

  const refresh = useCallback(() => {
    lastCheckedRef.current = new Date();
    setFormattedTimestamp(formatTimestamp(lastCheckedRef.current));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setFormattedTimestamp(formatTimestamp(lastCheckedRef.current));
    }, UPDATE_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return { formattedTimestamp, refresh };
}
